/**
 * Per-jurisdiction CURRENCY status — how live a jurisdiction's permit feed is,
 * NOT how much history it has. molluskio#4153 found that `newest_issued` /
 * last-permit-date is a false currency signal: a feed can stop updating yet look
 * current on a single recent permit plus deep history. This mirrors the coverage
 * believability gate's trailing-window rule (mollusk-interface#149) so the served
 * UI/API can label a thin-tail or stale feed instead of presenting it as current.
 *
 * Only the states derivable from the signals the interface already holds are
 * computed here: `current`, `thin_tail`, `stale`, `dateless`. The `future_dated`
 * and `year_only` states from #4153 need the upstream `currency_status` field
 * (mollusk-interface#150) and are added when it lands.
 */
/** Trailing-12-month volume floor, permits / 1k population / yr. Mirrors
 *  COVERAGE_GATE.trailingMinRatePer1kPerYr in apps/api (#149). Keep in sync. */
export const CURRENCY_TRAILING_MIN_PER_1K_PER_YR = 0.25;
/** A feed with no permit on/after (now − this many months) is stale. */
export const CURRENCY_RECENCY_MONTHS = 12;
export const CURRENCY_STATUSES = ['current', 'thin_tail', 'stale', 'dateless'];
/**
 * Classify a jurisdiction's feed currency. Precedence: dateless → stale →
 * thin_tail → current. `now` is the reference date (pass the artifact's
 * generated_at for determinism).
 */
export function deriveCurrencyStatus(s, now) {
    if (s.permit_count <= 0 || s.newest_permit_date == null)
        return 'dateless';
    const newest = new Date(`${s.newest_permit_date}T00:00:00Z`);
    if (Number.isNaN(newest.getTime()))
        return 'dateless';
    const cutoff = new Date(now);
    cutoff.setUTCMonth(cutoff.getUTCMonth() - CURRENCY_RECENCY_MONTHS);
    if (newest < cutoff)
        return 'stale';
    // Thin tail: looks recent, but trailing-year volume has collapsed (#4153).
    if (s.population != null && s.population > 0) {
        const per1k = ((s.permits_last_12mo ?? 0) / s.population) * 1000;
        if (per1k < CURRENCY_TRAILING_MIN_PER_1K_PER_YR)
            return 'thin_tail';
    }
    return 'current';
}
/** True only for a genuinely-current feed. */
export function isCurrent(status) {
    return status === 'current';
}
/** Human-facing label for the UI badge. */
export function currencyLabel(status) {
    switch (status) {
        case 'current':
            return 'Current';
        case 'thin_tail':
            return 'Thin recent activity';
        case 'stale':
            return 'Stale';
        case 'dateless':
            return 'Undated';
    }
}
//# sourceMappingURL=status.js.map